import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Fournisseur } from '../lib/api-types';

interface FournisseursPageProps {
  entrepriseId: string;
}

const inputClass = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
const labelClass = 'block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1';

interface FournisseurForm {
  nom: string;
  email: string;
  siret: string;
  telephone: string;
}

const emptyForm: FournisseurForm = { nom: '', email: '', siret: '', telephone: '' };

export default function FournisseursPage({ entrepriseId }: FournisseursPageProps) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [editFournisseur, setEditFournisseur] = useState<Fournisseur | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<FournisseurForm>(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState<Fournisseur | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: fournisseurs = [], isLoading } = useQuery({
    queryKey: ['fournisseurs', entrepriseId, search],
    queryFn: () => api.fournisseurs.list(entrepriseId, search || undefined),
  });

  const createMutation = useMutation({
    mutationFn: () => api.fournisseurs.create({
      entreprise_id: entrepriseId,
      nom: form.nom,
      email: form.email || undefined,
      siret: form.siret || undefined,
      telephone: form.telephone || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fournisseurs', entrepriseId] });
      setShowCreate(false);
      setForm(emptyForm);
      setError(null);
    },
    onError: (e: any) => setError(e.message || 'Erreur lors de la création'),
  });

  const updateMutation = useMutation({
    mutationFn: () => api.fournisseurs.update!(editFournisseur!.id, {
      entreprise_id: entrepriseId,
      nom: form.nom,
      email: form.email || undefined,
      siret: form.siret || undefined,
      telephone: form.telephone || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fournisseurs', entrepriseId] });
      setEditFournisseur(null);
      setForm(emptyForm);
      setError(null);
    },
    onError: (e: any) => setError(e.message || 'Erreur lors de la modification'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.fournisseurs.delete!(id, entrepriseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fournisseurs', entrepriseId] });
      setDeleteConfirm(null);
      setError(null);
    },
    onError: (e: any) => setError(e.message || 'Erreur lors de la suppression'),
  });

  const openEdit = (f: Fournisseur) => {
    setForm({
      nom: f.nom,
      email: f.email || '',
      siret: f.siret || '',
      telephone: f.telephone || '',
    });
    setEditFournisseur(f);
    setError(null);
  };

  const openCreate = () => {
    setForm(emptyForm);
    setShowCreate(true);
    setError(null);
  };

  const closeModal = () => {
    setEditFournisseur(null);
    setShowCreate(false);
    setForm(emptyForm);
    setError(null);
  };

  const isModalOpen = !!(editFournisseur || showCreate);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Fournisseurs</h2>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
        >
          + Nouveau fournisseur
        </button>
      </div>

      <div className="mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher par nom..."
          className={inputClass}
        />
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-400 py-8 text-center">Chargement...</div>
      ) : fournisseurs.length === 0 ? (
        <div className="text-sm text-gray-400 py-8 text-center bg-white rounded-xl border border-gray-100">
          Aucun fournisseur trouvé.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Nom</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Téléphone</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">SIRET</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {fournisseurs.map((f) => (
                <tr key={f.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-900 border-b border-gray-100">{f.nom}</td>
                  <td className="px-4 py-3 text-gray-500 border-b border-gray-100">{f.email || '-'}</td>
                  <td className="px-4 py-3 text-gray-500 border-b border-gray-100">{f.telephone || '-'}</td>
                  <td className="px-4 py-3 text-gray-500 border-b border-gray-100 text-xs font-mono">{f.siret || '-'}</td>
                  <td className="px-4 py-3 border-b border-gray-100">
                    <div className="flex items-center gap-2 justify-end">
                      <button onClick={() => openEdit(f)} className="text-gray-400 hover:text-blue-500 transition-colors" title="Modifier">✏️</button>
                      <button onClick={() => setDeleteConfirm(f)} className="text-gray-400 hover:text-red-500 transition-colors" title="Supprimer">🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editFournisseur ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}
            </h3>
            <div className="space-y-3">
              <div>
                <label className={labelClass}>Nom *</label>
                <input value={form.nom} onChange={(e) => setForm(f => ({ ...f, nom: e.target.value }))} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Téléphone</label>
                <input value={form.telephone} onChange={(e) => setForm(f => ({ ...f, telephone: e.target.value }))} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>SIRET</label>
                <input value={form.siret} onChange={(e) => setForm(f => ({ ...f, siret: e.target.value }))} className={inputClass} placeholder="14 chiffres" />
              </div>
            </div>
            {error && <p className="text-red-600 text-xs mt-3">{error}</p>}
            <div className="flex gap-3 mt-5">
              <button onClick={closeModal} className="flex-1 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Annuler</button>
              <button
                onClick={() => editFournisseur ? updateMutation.mutate() : createMutation.mutate()}
                disabled={!form.nom || createMutation.isPending || updateMutation.isPending}
                className="flex-1 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {(createMutation.isPending || updateMutation.isPending) ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Supprimer le fournisseur</h3>
            <p className="text-sm text-gray-600 mb-4">Supprimer <strong>{deleteConfirm.nom}</strong> ? Les transactions associées ne seront pas supprimées.</p>
            {error && <p className="text-red-600 text-xs mb-3">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Annuler</button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirm.id)}
                disabled={deleteMutation.isPending}
                className="flex-1 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50 transition-colors"
              >
                {deleteMutation.isPending ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
